import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import FieldSelectModule from './FieldSelectModule';
import IngestionGroupMultiPicker from './IngestionGroupMultiPicker';
import ToolCollisionConfirmModal from './ToolCollisionConfirmModal';
import './ToolEditor.css';
import {
  validateToolData,
  normalizeToolKind,
  clearErrorAfterDelay,
  generateErrorMessage
} from '../utils/errorHandling';
import { buildErrorDisplay } from '../utils/errorDetails';
import { API_BASE_URL, getAuthHeaders, listMcpServers, listMcpServerTools } from '../utils/api';
import { useGroup } from '../contexts/GroupContext';
import { WEB_SEARCH_PROVIDERS, normalizeWebSearchProvider } from '../utils/webSearchProviders';
import {
  parseToolIngestionGroupSlugs,
  fetchUnifiedKeysForGroup,
  intersectFieldLists,
  detectLocalFieldCollisions,
  checkToolFieldCollisionsRemote,
  MAX_TOOL_INGESTION_GROUPS,
} from '../utils/toolGroupKeys';
import YumlConfigFields from './YumlConfigFields';
import NameSlugFields from './NameSlugFields';
import ToolChainStepsEditor from './ToolChainStepsEditor';
import { slugifyName, getSlugConflictMessage, normalizeSlugInput } from '../utils/slugify';

const EMAIL_DRAFT_ACTIONS = [
  { id: 'save', label: 'Save draft (Zagger)' },
  { id: 'export_md', label: 'Export draft as markdown' },
  { id: 'gmail', label: 'Save draft to Gmail' },
];

const RESEARCH_ACTIONS = [
  { id: 'start', label: 'Start (queue a research job)' },
  { id: 'status', label: 'Status (poll a job)' },
  { id: 'report', label: 'Report (fetch finished report)' },
];

const CRON_PRESETS = [
  { id: 'every-15', label: 'Every 15 minutes', expr: '*/15 * * * *' },
  { id: 'hourly', label: 'Every hour at :00 UTC', expr: '0 * * * *' },
  { id: 'daily', label: 'Daily at 00:00 UTC', expr: '0 0 * * *' },
  { id: 'weekdays-9', label: 'Weekdays at 09:00 UTC', expr: '0 9 * * 1-5' },
];

function isGenericSchedulerPrompt(p) {
  const loc = String(p?.page_location || '').trim().toLowerCase();
  return loc !== 'main' && loc !== 'review' && loc !== 'management';
}

const RESEARCH_MODES = [
  'general_report',
  'competitive_analysis',
  'grant_research',
  'investor_research',
  'prediction_market_contest',
  'customer_discovery',
  'document_synthesis',
];

const RESEARCH_PROVIDERS = [
  { id: 'openai', label: 'OpenAI' },
  { id: 'anthropic', label: 'Anthropic' },
  { id: 'zagger', label: 'Zagger' },
];

const HTTP_METHODS = ['GET', 'POST', 'PUT', 'PATCH', 'DELETE', 'HEAD'];
const HTTP_AUTH_TOKEN_MASK = '••••••••';

function headersToJsonText(headers) {
  try {
    const obj = headers && typeof headers === 'object' && !Array.isArray(headers) ? headers : {};
    return JSON.stringify(obj, null, 2);
  } catch (_) {
    return '{}';
  }
}

function parseHeadersJsonText(text) {
  const raw = String(text || '').trim();
  if (!raw) return {};
  const parsed = JSON.parse(raw);
  if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
    throw new Error('Headers must be a JSON object');
  }
  const out = {};
  for (const [k, v] of Object.entries(parsed)) {
    const key = String(k || '').trim();
    if (!key) continue;
    out[key] = v == null ? '' : String(v);
  }
  return out;
}

const EMPTY_HTTP_REQUEST_FIELDS = {
  http_method: 'GET',
  request_url: '',
  http_headers: {},
  http_headers_json: '{}',
  auth_type: 'none',
  auth_token: '',
  auth_header_name: 'X-Api-Key',
  body_template: '',
  timeout_ms: 20000,
  max_body_chars: 200000,
  max_chars: 50000,
  user_agent: '',
  response_as: 'auto',
  batch_size: 1,
};

function normalizeResearchAction(raw) {
  const a = String(raw || 'start').trim().toLowerCase();
  if (a === 'status' || a === 'get_status') return 'status';
  if (a === 'report' || a === 'fetch' || a === 'get_report' || a === 'retrieve') return 'report';
  return 'start';
}

/** Infer action from slug so fetch/status tools hydrate correctly even if metadata was never saved. */
function inferResearchActionFromSlug(slug) {
  const s = String(slug || '').trim().toLowerCase().replace(/\s+/g, '-');
  if (!s) return null;
  if (/(^|[_-])(fetch|report|retrieve)([_-]|$)/.test(s)) return 'report';
  if (/(^|[_-])status([_-]|$)/.test(s)) return 'status';
  if (/(^|[_-])(start|conduct|begin|run)([_-]|$)/.test(s)) return 'start';
  return null;
}

function resolveResearchActionForEditor(raw, slug) {
  const inferred = inferResearchActionFromSlug(slug);
  const hasExplicit = String(raw ?? '').trim() !== '';
  const normalized = normalizeResearchAction(raw);
  if (inferred && inferred !== 'start' && (!hasExplicit || normalized === 'start')) {
    return inferred;
  }
  return normalized;
}

function parseToolMetadata(raw) {
  if (!raw) return {};
  if (typeof raw === 'object' && !Array.isArray(raw)) return raw;
  if (typeof raw === 'string') {
    try {
      const parsed = JSON.parse(raw);
      return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : {};
    } catch (_) {
      return {};
    }
  }
  return {};
}

function ToolEditor({
  isOpen,
  onClose,
  onSave,
  tool = null,
  loading = false,
  existingToolSlugs = [],
}) {
  const { activeSuffix } = useGroup();
  const [toolSuffix, setToolSuffix] = useState(activeSuffix || 'rsmg_sales');
  const [availableHorizons, setAvailableHorizons] = useState([]);
  const [formData, setFormData] = useState({
    slug: '',
    name: '',
    description: '',
    tool_kind: 'search',
    search_field: '',
    return_fields: [],
    ingestion_group_slugs: [],
    search_provider: 'brave',
    research_action: 'start',
    default_mode: '',
    default_provider: '',
    default_model: '',
    failure_policy: 'stop',
    chain_steps: [],
    period_hours: 24,
    cron_expr: '0 0 * * *',
    prompt_id: '',
    prompt_instructions: '',
    scheduler_run_mode: 'chain',
    ...EMPTY_HTTP_REQUEST_FIELDS,
    mcp_server_id: '',
    mcp_tool_name: '',
    default_arguments: '',
    email_action: 'save',
    default_tone: '',
    is_private: true,
    allow_create: true,
    yuml_config: { enabled: false, template_id: null, summary_keys: [], emit_on: 'after_output' },
  });

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [submitErrorDetails, setSubmitErrorDetails] = useState(null);
  const [fieldErrors, setFieldErrors] = useState({});
  const errorTimeoutRef = useRef(null);
  const [mcpServers, setMcpServers] = useState([]);
  const [mcpTools, setMcpTools] = useState([]);
  const [mcpToolsLoading, setMcpToolsLoading] = useState(false);
  const [mcpToolsError, setMcpToolsError] = useState('');
  const [mcpDebug, setMcpDebug] = useState(null);
  const [schedulerPrompts, setSchedulerPrompts] = useState([]);
  const [schedulerPromptsError, setSchedulerPromptsError] = useState('');

  const [groupRawFields, setGroupRawFields] = useState([]);
  const [groupReturnFields, setGroupReturnFields] = useState([]);
  const [fileKeysConvertToText, setFileKeysConvertToText] = useState([]);
  const [groupMetaFromKeys, setGroupMetaFromKeys] = useState(null);
  const [keysLoading, setKeysLoading] = useState(false);

  const [collisionModalOpen, setCollisionModalOpen] = useState(false);
  const [pendingCollisions, setPendingCollisions] = useState({ collisions: [], pairs: [] });
  const [pendingSavePayload, setPendingSavePayload] = useState(null);
  const [slugTouched, setSlugTouched] = useState(false);

  const selectedGroupSlugs = useMemo(() => {
    const fromForm = Array.isArray(formData.ingestion_group_slugs) ? formData.ingestion_group_slugs : [];
    if (fromForm.length) return fromForm.map((s) => String(s).trim()).filter(Boolean);
    return parseToolIngestionGroupSlugs(tool);
  }, [formData.ingestion_group_slugs, tool]);

  const isAnalyticsTool = formData.tool_kind === 'analytics';
  const isFilterTool = formData.tool_kind === 'filter';
  const isWebSearchTool = formData.tool_kind === 'web_search';
  const isResearchTool = formData.tool_kind === 'research';
  const isToolChain = formData.tool_kind === 'tool_chain';
  const isScheduler = formData.tool_kind === 'scheduler';
  const isHttpRequest = formData.tool_kind === 'http_request';
  const isWebpageScrape = formData.tool_kind === 'webpage_scrape';
  const isEntryUpdate = formData.tool_kind === 'entry_update';
  const isEntryCreate = formData.tool_kind === 'entry_create';
  const isEntryWriteTool = isEntryUpdate || isEntryCreate;
  const isMcpCall = formData.tool_kind === 'mcp_call';
  const isEmailDraft = formData.tool_kind === 'email_draft';
  const isDataBackedTool =
    !isWebSearchTool &&
    !isResearchTool &&
    !isToolChain &&
    !isScheduler &&
    !isHttpRequest &&
    !isWebpageScrape &&
    !isMcpCall &&
    !isEmailDraft;
  const isExactKeyTool = isAnalyticsTool || isFilterTool;
  const needsWritableAllowlist = isExactKeyTool || isEntryWriteTool;

  useEffect(() => {
    const loadHorizons = async () => {
      try {
        const headers = await getAuthHeaders();
        const resp = await fetch(`${API_BASE_URL}/casc/v1/api/horizon-management/horizons`, {
          method: 'POST',
          headers
        });
        const data = await resp.json();
        if (data.success && data.horizons) {
          setAvailableHorizons(data.horizons);
        }
      } catch (err) {
        console.error('Error loading horizons:', err);
      }
    };
    loadHorizons();
  }, []);

  useEffect(() => {
    if (!isScheduler || !isOpen) return undefined;
    let cancelled = false;
    (async () => {
      setSchedulerPromptsError('');
      try {
        const headers = await getAuthHeaders();
        const resp = await fetch(`${API_BASE_URL}/casc/v1/api/prompts`, {
          method: 'POST',
          headers,
          body: JSON.stringify({}),
        });
        const data = await resp.json().catch(() => ({}));
        if (cancelled) return;
        if (!resp.ok || !data?.success) {
          setSchedulerPromptsError(data?.error || data?.message || 'Failed to load prompts');
          setSchedulerPrompts([]);
          return;
        }
        const list = Array.isArray(data.prompts) ? data.prompts : [];
        setSchedulerPrompts(list.filter(isGenericSchedulerPrompt));
      } catch (err) {
        if (!cancelled) {
          setSchedulerPromptsError(err.message || 'Failed to load prompts');
          setSchedulerPrompts([]);
        }
      }
    })();
    return () => { cancelled = true; };
  }, [isScheduler, isOpen, toolSuffix]);

  useEffect(() => {
    if (!isMcpCall) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const data = await listMcpServers();
        if (cancelled) return;
        const servers = [...(data.owned || []), ...(data.public || [])];
        setMcpServers(servers);
      } catch (err) {
        if (!cancelled) setMcpToolsError(err.message || 'Failed to load MCP servers');
      }
    })();
    return () => { cancelled = true; };
  }, [isMcpCall]);

  useEffect(() => {
    if (!isMcpCall || !formData.mcp_server_id) {
      setMcpTools([]);
      setMcpDebug(null);
      return undefined;
    }
    let cancelled = false;
    setMcpToolsLoading(true);
    setMcpToolsError('');
    (async () => {
      try {
        const data = await listMcpServerTools(formData.mcp_server_id, { allowInactive: true });
        if (cancelled) return;
        setMcpTools(Array.isArray(data.tools) ? data.tools : []);
        setMcpDebug({
          serverName: data.serverName,
          transport: data.transport,
          toolCount: data.toolCount,
          durationMs: data.durationMs,
          tools: data.tools || [],
        });
      } catch (err) {
        if (!cancelled) {
          setMcpTools([]);
          setMcpToolsError(err.message || 'Failed to list MCP tools');
          setMcpDebug(null);
        }
      } finally {
        if (!cancelled) setMcpToolsLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [isMcpCall, formData.mcp_server_id]);

  useEffect(() => {
    if (tool) {
      const slugs = parseToolIngestionGroupSlugs(tool);
      const meta = parseToolMetadata(tool.metadata);
      setFormData({
        slug: tool.slug || '',
        name: tool.name || '',
        description: tool.description || '',
        tool_kind: normalizeToolKind(tool.tool_kind),
        search_field: tool.search_field || '',
        return_fields: tool.return_fields || [],
        ingestion_group_slugs: slugs,
        search_provider: normalizeWebSearchProvider(meta.provider || tool.search_provider),
        research_action: resolveResearchActionForEditor(
          meta.research_action || tool.research_action,
          tool.slug || tool.name
        ),
        default_mode: meta.default_mode || tool.default_mode || '',
        default_provider: String(
          meta.default_provider || tool.default_provider || ''
        ).trim().toLowerCase(),
        default_model: meta.default_model || tool.default_model || '',
        failure_policy: meta.failure_policy || tool.failure_policy || 'stop',
        chain_steps: Array.isArray(meta.steps)
          ? meta.steps
          : (Array.isArray(tool.chain_steps) ? tool.chain_steps : []),
        period_hours: meta.period_hours || tool.period_hours || 24,
        cron_expr: meta.cron_expr || tool.cron_expr || '0 0 * * *',
        prompt_id: meta.prompt_id || tool.prompt_id || '',
        prompt_instructions: meta.prompt_instructions || tool.prompt_instructions || '',
        scheduler_run_mode: meta.prompt_id ? 'prompt' : 'chain',
        http_method: String(meta.method || tool.http_method || 'GET').toUpperCase(),
        request_url: meta.request_url || tool.request_url || '',
        http_headers: meta.headers && typeof meta.headers === 'object' ? meta.headers : {},
        http_headers_json: headersToJsonText(meta.headers || tool.http_headers),
        auth_type: meta.auth_type || tool.auth_type || 'none',
        auth_token:
          meta.auth_token_set || (meta.auth_token && String(meta.auth_token).trim())
            ? (meta.auth_token && String(meta.auth_token).trim()) || HTTP_AUTH_TOKEN_MASK
            : '',
        auth_header_name: meta.auth_header_name || tool.auth_header_name || 'X-Api-Key',
        body_template: meta.body_template != null ? String(meta.body_template) : (tool.body_template || ''),
        timeout_ms: meta.timeout_ms ?? tool.timeout_ms ?? 20000,
        max_body_chars: meta.max_body_chars ?? tool.max_body_chars ?? 200000,
        max_chars: meta.max_chars ?? tool.max_chars ?? 50000,
        user_agent: meta.user_agent || tool.user_agent || '',
        response_as: meta.response_as || tool.response_as || 'auto',
        batch_size: meta.batch_size ?? tool.batch_size ?? 1,
        mcp_server_id: meta.mcp_server_id || tool.mcp_server_id || '',
        mcp_tool_name: meta.mcp_tool_name || tool.mcp_tool_name || '',
        default_arguments: meta.default_arguments
          ? JSON.stringify(meta.default_arguments, null, 2)
          : (tool.default_arguments
            ? (typeof tool.default_arguments === 'string'
              ? tool.default_arguments
              : JSON.stringify(tool.default_arguments, null, 2))
            : ''),
        email_action: meta.email_action || tool.email_action || 'save',
        default_tone: meta.default_tone || tool.default_tone || '',
        is_private: tool.is_private !== undefined ? tool.is_private : true,
        allow_create: meta.allow_create !== false,
        yuml_config: tool.yuml_config && typeof tool.yuml_config === 'object'
          ? tool.yuml_config
          : { enabled: false, template_id: null, summary_keys: [], emit_on: 'after_output' },
      });
      setToolSuffix(tool.suffix || activeSuffix || 'rsmg_sales');
    } else {
      setFormData({
        slug: '',
        name: '',
        description: '',
        tool_kind: 'search',
        search_field: '',
        return_fields: [],
        ingestion_group_slugs: [],
        search_provider: 'brave',
        research_action: 'start',
        default_mode: '',
        default_provider: '',
        default_model: '',
        failure_policy: 'stop',
        chain_steps: [],
        period_hours: 24,
        cron_expr: '0 0 * * *',
        prompt_id: '',
        prompt_instructions: '',
        scheduler_run_mode: 'chain',
        ...EMPTY_HTTP_REQUEST_FIELDS,
        mcp_server_id: '',
        mcp_tool_name: '',
        default_arguments: '',
        email_action: 'save',
        default_tone: '',
        is_private: true,
        allow_create: true,
        yuml_config: { enabled: false, template_id: null, summary_keys: [], emit_on: 'after_output' },
      });
    }
    setSlugTouched(!!tool);
    setErrors({});
  }, [tool, activeSuffix]);

  const slugConflict = useMemo(() => {
    if (tool) return null;
    return getSlugConflictMessage(formData.slug, formData.name, existingToolSlugs);
  }, [tool, formData.slug, formData.name, existingToolSlugs]);

  useEffect(() => {
    if (!isOpen) {
      setSlugTouched(false);
    }
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) {
      setErrors({});
      setFieldErrors({});
      setSubmitError('');
      setIsSubmitting(false);
      setCollisionModalOpen(false);
      setPendingSavePayload(null);
      if (errorTimeoutRef.current) {
        clearTimeout(errorTimeoutRef.current);
        errorTimeoutRef.current = null;
      }
    }
  }, [isOpen]);

  useEffect(() => () => {
    if (errorTimeoutRef.current) clearTimeout(errorTimeoutRef.current);
  }, []);

  const validateField = useCallback((fieldName, value) => {
    const tempFormData = { ...formData, [fieldName]: value };
    const validation = validateToolData(tempFormData, !!tool);
    setErrors((prev) => {
      const updated = { ...prev };
      if (validation.errors[fieldName]) updated[fieldName] = validation.errors[fieldName];
      else delete updated[fieldName];
      if (fieldName === 'ingestion_group_slugs' && validation.errors.ingestion_group_slugs) {
        updated.ingestion_group_slugs = validation.errors.ingestion_group_slugs;
      }
      return updated;
    });
    return !validation.errors[fieldName];
  }, [formData, tool]);

  const validateAllFields = () => {
    const validation = validateToolData(formData, !!tool);
    setErrors(validation.errors);
    return validation.isValid;
  };

  const handleHorizonChange = (newSuffix) => {
    setToolSuffix(newSuffix);
    setFormData((prev) => ({
      ...prev,
      ingestion_group_slugs: [],
      search_field: '',
      return_fields: [],
      tool_kind: prev.tool_kind || 'search'
    }));
    setGroupRawFields([]);
    setGroupReturnFields([]);
    setFileKeysConvertToText([]);
  };

  const handleInputChange = (field, value) => {
    setFormData((prev) => {
      const next = { ...prev, [field]: value };
      if (field === 'tool_kind') {
        const validation = validateToolData(next, !!tool);
        setErrors(validation.errors);
        // When switching to research, prefer slug-inferred action over stale "start"
        if (normalizeToolKind(value) === 'research') {
          next.research_action = resolveResearchActionForEditor(
            next.research_action,
            next.slug || next.name
          );
        }
      }
      if ((field === 'slug' || field === 'name') && normalizeToolKind(next.tool_kind) === 'research') {
        const inferred = inferResearchActionFromSlug(next.slug || next.name);
        if (inferred && inferred !== 'start' && next.research_action === 'start') {
          next.research_action = inferred;
        }
      }
      return next;
    });
    if (submitError) setSubmitError('');
    if (field !== 'tool_kind') validateField(field, value);
    if (fieldErrors[field]) {
      setFieldErrors((prev) => {
        const next = { ...prev };
        delete next[field];
        return next;
      });
    }
  };

  const handleNameChange = (value) => {
    setFormData((prev) => {
      const next = { ...prev, name: value };
      if (!tool && !slugTouched) {
        next.slug = slugifyName(value);
      }
      return next;
    });
    if (submitError) setSubmitError('');
    validateField('name', value);
    if (!tool && !slugTouched) {
      validateField('slug', slugifyName(value));
    }
  };

  const handleSlugChange = (value) => {
    setSlugTouched(true);
    const normalized = normalizeSlugInput(value);
    handleInputChange('slug', normalized);
  };

  const handleFieldError = (field, error) => {
    setFieldErrors((prev) => ({ ...prev, [field]: error }));
  };

  const clearFieldError = (field) => {
    setFieldErrors((prev) => {
      const next = { ...prev };
      delete next[field];
      return next;
    });
  };

  useEffect(() => {
    if (!selectedGroupSlugs.length) {
      setGroupRawFields([]);
      setGroupReturnFields([]);
      setFileKeysConvertToText([]);
      setGroupMetaFromKeys(null);
      return undefined;
    }
    let aborted = false;
    setKeysLoading(true);
    (async () => {
      try {
        const results = await Promise.all(
          selectedGroupSlugs.map((g) => fetchUnifiedKeysForGroup(toolSuffix, g))
        );
        if (aborted) return;
        const searchLists = results.map((r) => r.searchOptions);
        const returnLists = results.map((r) => r.returnOptions);
        setGroupRawFields(intersectFieldLists(searchLists));
        setGroupReturnFields(intersectFieldLists(returnLists));
        const fileUnion = new Set();
        results.forEach((r) => (r.fileKeysConvertToText || []).forEach((k) => fileUnion.add(k)));
        setFileKeysConvertToText(Array.from(fileUnion));
        setGroupMetaFromKeys(
          selectedGroupSlugs.length === 1 && results[0]?.meta
            ? results[0].meta
            : { name: null, is_private: null, multi: true, count: selectedGroupSlugs.length }
        );
      } catch (e) {
        if (!aborted) {
          setGroupRawFields([]);
          setGroupReturnFields([]);
          setFileKeysConvertToText([]);
          setGroupMetaFromKeys(null);
        }
      } finally {
        if (!aborted) setKeysLoading(false);
      }
    })();
    return () => { aborted = true; };
  }, [selectedGroupSlugs, toolSuffix]);

  useEffect(() => {
    if (!selectedGroupSlugs.length) return;
    if (formData.name) validateField('name', formData.name);
    if (!tool && formData.slug) validateField('slug', formData.slug);
    validateField('ingestion_group_slugs', formData.ingestion_group_slugs);
    if (formData.search_field) validateField('search_field', formData.search_field);
    if (Array.isArray(formData.return_fields) && formData.return_fields.length > 0) {
      validateField('return_fields', formData.return_fields);
    }
  }, [
    groupRawFields,
    groupReturnFields,
    selectedGroupSlugs,
    formData.ingestion_group_slugs,
    formData.name,
    formData.slug,
    formData.search_field,
    formData.return_fields,
    tool,
    validateField
  ]);

  const runCollisionCheck = async () => {
    if (selectedGroupSlugs.length < 2) {
      return { collisions: [], pairs: [], hasCollisions: false };
    }
    try {
      const remote = await checkToolFieldCollisionsRemote({
        suffix: toolSuffix,
        ingestion_group_slugs: selectedGroupSlugs,
        search_field: formData.search_field,
        return_fields: formData.return_fields,
      });
      return {
        collisions: remote.collisions || [],
        pairs: remote.pairs || [],
        hasCollisions: Boolean(remote.hasCollisions),
      };
    } catch (_) {
      const keysByGroup = {};
      for (const g of selectedGroupSlugs) {
        try {
          const r = await fetchUnifiedKeysForGroup(toolSuffix, g);
          keysByGroup[g] = new Set([...r.searchOptions, ...r.returnOptions]);
        } catch (e) {
          keysByGroup[g] = new Set();
        }
      }
      return detectLocalFieldCollisions(selectedGroupSlugs, keysByGroup, {
        search_field: formData.search_field,
        return_fields: formData.return_fields,
      });
    }
  };

  const performSave = async (payload) => {
    setIsSubmitting(true);
    try {
      await onSave(payload);
    } catch (error) {
      console.error('Error saving tool:', error);
      const errorMessage = generateErrorMessage(
        error,
        tool ? 'updating tool' : 'creating tool',
        error.response
      );
      const { details } = buildErrorDisplay(error, error.response);
      setSubmitError(errorMessage);
      setSubmitErrorDetails(details);
      errorTimeoutRef.current = clearErrorAfterDelay(() => {
        setSubmitError('');
        setSubmitErrorDetails(null);
      }, 12000);
    } finally {
      setIsSubmitting(false);
      setCollisionModalOpen(false);
      setPendingSavePayload(null);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitError('');
    setSubmitErrorDetails(null);
    if (slugConflict) {
      setSubmitError(slugConflict);
      return;
    }
    if (!validateAllFields()) {
      setSubmitError('Please correct the validation errors above');
      return;
    }
    if (Object.keys(fieldErrors).length > 0) {
      setSubmitError('Please resolve field validation errors before submitting');
      return;
    }

    const payload = {
      ...formData,
      ingestion_group_slugs: selectedGroupSlugs,
      ingestion_group_slug: selectedGroupSlugs[0] || '',
      suffix: toolSuffix,
    };
    if (formData.tool_kind === 'entry_update') {
      payload.search_field = '_entry_update';
    }
    if (formData.tool_kind === 'entry_create') {
      payload.search_field = '_entry_create';
    }
    if (formData.tool_kind === 'scheduler') {
      if (formData.scheduler_run_mode === 'prompt') {
        payload.chain_steps = [];
      } else {
        payload.prompt_id = '';
        payload.prompt_instructions = '';
      }
    }
    if (formData.tool_kind === 'http_request') {
      payload.search_field = '_http_request';
      try {
        payload.http_headers = parseHeadersJsonText(formData.http_headers_json);
      } catch (err) {
        setSubmitError(err.message || 'Invalid headers JSON');
        setErrors((prev) => ({ ...prev, http_headers_json: err.message || 'Invalid JSON object' }));
        return;
      }
    }
    if (formData.tool_kind === 'webpage_scrape') {
      payload.search_field = '_webpage_scrape';
      payload.timeout_ms = formData.timeout_ms || 15000;
      payload.max_chars = formData.max_chars || 50000;
      if (formData.user_agent && String(formData.user_agent).trim()) {
        payload.user_agent = String(formData.user_agent).trim();
      }
    }

    const collisionResult = await runCollisionCheck();
    if (collisionResult.hasCollisions) {
      setPendingCollisions({
        collisions: collisionResult.collisions,
        pairs: collisionResult.pairs,
      });
      setPendingSavePayload(payload);
      setCollisionModalOpen(true);
      return;
    }

    await performSave(payload);
  };

  const handleCollisionSave = () => {
    if (pendingSavePayload) performSave(pendingSavePayload);
  };

  const handleCollisionFix = () => {
    setCollisionModalOpen(false);
    setPendingSavePayload(null);
  };

  const handleClose = () => {
    if (!isSubmitting) onClose();
  };

  if (!isOpen) return null;

  const isEditMode = !!tool;

  return (
    <>
      <div className="modal show">
        <div className="modal-content tool-editor-modal">
          <div className="modal-header">
            <h2 className="modal-title">
              <i className="fas fa-tools" />
              {isEditMode ? 'Edit Tool' : 'Create New Tool'}
            </h2>
            <button type="button" className="close" onClick={handleClose} disabled={isSubmitting}>
              &times;
            </button>
          </div>

          <form onSubmit={handleSubmit} noValidate>
            <div className="modal-body">
              {submitError && (
                <div className="alert alert-error tool-editor-error" style={{ marginBottom: '20px' }}>
                  <i className="fas fa-exclamation-circle" />
                  <div className="tool-editor-error-body">
                    <div>{submitError}</div>
                    {submitErrorDetails ? (
                      <pre className="alert-error-details">{submitErrorDetails}</pre>
                    ) : null}
                  </div>
                </div>
              )}
              <div className="tool-form">
                <NameSlugFields
                  name={formData.name}
                  slug={formData.slug}
                  onNameChange={handleNameChange}
                  onSlugChange={handleSlugChange}
                  slugConflict={slugConflict}
                  nameError={errors.name}
                  slugError={errors.slug}
                  isEdit={isEditMode}
                  nameId="toolName"
                  slugId="toolSlug"
                  namePlaceholder="e.g., Document Search Tool"
                  slugPlaceholder="e.g., document-search"
                  nameDisabled={isSubmitting}
                  slugDisabled={isSubmitting}
                />

                <div className="form-group">
                  <label htmlFor="toolDescription">Description</label>
                  <textarea
                    id="toolDescription"
                    value={formData.description}
                    onChange={(e) => handleInputChange('description', e.target.value)}
                    disabled={isSubmitting}
                    placeholder="Description of what this tool does"
                    rows="3"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="toolKind">Tool type</label>
                  <select
                    id="toolKind"
                    className="form-control"
                    value={formData.tool_kind}
                    onChange={(e) => handleInputChange('tool_kind', e.target.value)}
                    disabled={isSubmitting}
                  >
                    <option value="search">Search (vector similarity)</option>
                    <option value="analytics">Analytics (SQL aggregates in chat)</option>
                    <option value="filter">Filter (exact field match → rows)</option>
                    <option value="web_search">Web search (internet — Brave, Serper, or Tavily)</option>
                    <option value="research">Deep Research (background investigation job)</option>
                    <option value="tool_chain">Tool chain (run tools in sequence)</option>
                    <option value="scheduler">Scheduler (cron: tools or a prompt)</option>
                    <option value="http_request">HTTP request (configured API call)</option>
                    <option value="webpage_scrape">Webpage scrape (browser-like fetch; flags scrape denials)</option>
                    <option value="entry_update">Entry update / upsert (chat confirm; update or create by entry_id)</option>
                    <option value="entry_create">Entry create (always creates a new row)</option>
                    <option value="mcp_call">MCP call (connected MCP server tool)</option>
                    <option value="email_draft">Email draft (save / export / Gmail)</option>
                  </select>
                </div>

                <div className="form-group">
                  <label htmlFor="toolHorizon">
                    Horizon <span className="required">*</span>
                  </label>
                  <select
                    id="toolHorizon"
                    value={toolSuffix}
                    onChange={(e) => handleHorizonChange(e.target.value)}
                    disabled={isEditMode || isSubmitting}
                    className="form-control"
                  >
                    {availableHorizons.length === 0 ? (
                      <option value={toolSuffix}>{toolSuffix || 'Loading horizons…'}</option>
                    ) : null}
                    {availableHorizons.map((horizon) => (
                      <option key={horizon.suffix} value={horizon.suffix}>
                        {horizon.description || horizon.suffix}
                      </option>
                    ))}
                    {toolSuffix
                      && availableHorizons.length > 0
                      && !availableHorizons.some((h) => h.suffix === toolSuffix) ? (
                      <option value={toolSuffix}>{toolSuffix} (current)</option>
                    ) : null}
                  </select>
                  {isEditMode && (
                    <small className="form-help">Horizon cannot be changed after creation</small>
                  )}
                </div>

                {isDataBackedTool && (
                  <div className="form-group">
                    <label>
                      Ingestion groups <span className="required">*</span>
                    </label>
                    <IngestionGroupMultiPicker
                      suffix={toolSuffix}
                      selectedSlugs={selectedGroupSlugs}
                      onChange={(slugs) => handleInputChange('ingestion_group_slugs', slugs)}
                      disabled={isSubmitting}
                      error={errors.ingestion_group_slugs || null}
                    />
                    <small className="form-help">
                      Add one or more groups (up to {MAX_TOOL_INGESTION_GROUPS}). Field pickers show keys
                      shared by every selected group.
                    </small>
                  </div>
                )}

                {isWebSearchTool && (
                  <div className="form-group">
                    <label htmlFor="searchProvider">Search provider</label>
                    <select
                      id="searchProvider"
                      className="form-control"
                      value={formData.search_provider}
                      onChange={(e) => handleInputChange('search_provider', e.target.value)}
                      disabled={isSubmitting}
                    >
                      {WEB_SEARCH_PROVIDERS.map((provider) => (
                        <option key={provider.id} value={provider.id}>
                          {provider.name}
                        </option>
                      ))}
                    </select>
                  </div>
                )}

                {isResearchTool && (
                  <>
                    <div className="form-group">
                      <label htmlFor="researchAction">Research action</label>
                      <select
                        id="researchAction"
                        className="form-control"
                        value={formData.research_action}
                        onChange={(e) => handleInputChange('research_action', e.target.value)}
                        disabled={isSubmitting}
                      >
                        {RESEARCH_ACTIONS.map((action) => (
                          <option key={action.id} value={action.id}>
                            {action.label}
                          </option>
                        ))}
                      </select>
                      <small className="form-help">
                        Use <strong>Start</strong> to queue research, <strong>Status</strong> to poll,
                        and <strong>Report</strong> / fetch to retrieve results. Tools whose slug contains
                        &quot;fetch&quot; or &quot;status&quot; are treated as report/status even if Start was saved by mistake.
                      </small>
                      {errors.research_action && (
                        <small className="form-error">{errors.research_action}</small>
                      )}
                    </div>
                    {formData.research_action === 'start' && (
                      <>
                        <div className="form-group">
                          <label htmlFor="researchDefaultMode">Default mode (optional)</label>
                          <div className="tool-editor-clearable-control">
                            <select
                              id="researchDefaultMode"
                              className="form-control"
                              value={formData.default_mode || ''}
                              onChange={(e) => handleInputChange('default_mode', e.target.value)}
                              disabled={isSubmitting}
                            >
                              <option value="">No default (server chooses)</option>
                              {RESEARCH_MODES.map((mode) => (
                                <option key={mode} value={mode}>
                                  {mode}
                                </option>
                              ))}
                            </select>
                            {Boolean(formData.default_mode) && (
                              <button
                                type="button"
                                className="tool-editor-clear-btn"
                                onClick={() => handleInputChange('default_mode', '')}
                                disabled={isSubmitting}
                                title="Clear default mode"
                                aria-label="Clear default mode"
                              >
                                ×
                              </button>
                            )}
                          </div>
                        </div>
                        <div className="form-group">
                          <label htmlFor="researchDefaultProvider">Default provider (optional)</label>
                          <div className="tool-editor-clearable-control">
                            <select
                              id="researchDefaultProvider"
                              className="form-control"
                              value={formData.default_provider || ''}
                              onChange={(e) => handleInputChange('default_provider', e.target.value)}
                              disabled={isSubmitting}
                            >
                              <option value="">Use my active chat model (Settings)</option>
                              {RESEARCH_PROVIDERS.map((provider) => (
                                <option key={provider.id} value={provider.id}>
                                  {provider.label}
                                </option>
                              ))}
                            </select>
                            {Boolean(formData.default_provider) && (
                              <button
                                type="button"
                                className="tool-editor-clear-btn"
                                onClick={() => handleInputChange('default_provider', '')}
                                disabled={isSubmitting}
                                title="Clear default provider"
                                aria-label="Clear default provider"
                              >
                                ×
                              </button>
                            )}
                          </div>
                          <small className="form-help">
                            Leave blank to use the provider/model you selected under Settings → active chat model.
                          </small>
                        </div>
                        <div className="form-group">
                          <label htmlFor="researchDefaultModel">Default model (optional)</label>
                          <div className="tool-editor-clearable-control">
                            <input
                              id="researchDefaultModel"
                              type="text"
                              className="form-control"
                              value={formData.default_model}
                              onChange={(e) => handleInputChange('default_model', e.target.value)}
                              disabled={isSubmitting}
                              placeholder="Leave blank to use my active chat model"
                            />
                            {Boolean(String(formData.default_model || '').trim()) && (
                              <button
                                type="button"
                                className="tool-editor-clear-btn"
                                onClick={() => handleInputChange('default_model', '')}
                                disabled={isSubmitting}
                                title="Clear default model"
                                aria-label="Clear default model"
                              >
                                ×
                              </button>
                            )}
                          </div>
                        </div>
                      </>
                    )}
                  </>
                )}

                {isToolChain && (
                  <ToolChainStepsEditor
                    steps={Array.isArray(formData.chain_steps) ? formData.chain_steps : []}
                    failurePolicy={formData.failure_policy || 'stop'}
                    onChangeSteps={(chain_steps) => handleInputChange('chain_steps', chain_steps)}
                    onChangeFailurePolicy={(failure_policy) => handleInputChange('failure_policy', failure_policy)}
                    disabled={isSubmitting}
                    excludeToolId={tool?.id || null}
                    excludeToolSlug={formData.slug || null}
                    error={errors.chain_steps || null}
                  />
                )}

                {isScheduler && (
                  <>
                    <div className="form-group">
                      <label htmlFor="cronExpr">
                        Cron schedule (UTC) <span className="required">*</span>
                      </label>
                      <input
                        id="cronExpr"
                        className="form-control"
                        value={formData.cron_expr || ''}
                        onChange={(e) => handleInputChange('cron_expr', e.target.value)}
                        disabled={isSubmitting}
                        placeholder="*/15 * * * *"
                        spellCheck={false}
                      />
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 8 }}>
                        {CRON_PRESETS.map((p) => (
                          <button
                            key={p.id}
                            type="button"
                            className="btn btn-secondary btn-sm"
                            disabled={isSubmitting}
                            onClick={() => handleInputChange('cron_expr', p.expr)}
                          >
                            {p.label}
                          </button>
                        ))}
                      </div>
                      <small className="form-help">
                        Five fields: minute hour day-of-month month day-of-week. Minutes must be
                        multiples of 15 (0, 15, 30, 45). Next run is the next clock time in UTC,
                        not “last start + interval”. See <a href="https://crontab.guru/" target="_blank" rel="noreferrer">crontab.guru</a>.
                      </small>
                      {errors.cron_expr && <div className="field-error">{errors.cron_expr}</div>}
                    </div>
                    <div className="form-group">
                      <label>What to run</label>
                      <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
                        <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}>
                          <input
                            type="radio"
                            name="scheduler_run_mode"
                            checked={(formData.scheduler_run_mode || 'chain') === 'chain'}
                            disabled={isSubmitting}
                            onChange={() => handleInputChange('scheduler_run_mode', 'chain')}
                          />
                          Tool chain (no LLM)
                        </label>
                        <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}>
                          <input
                            type="radio"
                            name="scheduler_run_mode"
                            checked={formData.scheduler_run_mode === 'prompt'}
                            disabled={isSubmitting}
                            onChange={() => handleInputChange('scheduler_run_mode', 'prompt')}
                          />
                          Prompt (instructions + attached tools)
                        </label>
                      </div>
                    </div>
                    {formData.scheduler_run_mode === 'prompt' ? (
                      <>
                        <div className="form-group">
                          <label htmlFor="schedulerPrompt">
                            Prompt <span className="required">*</span>
                          </label>
                          <select
                            id="schedulerPrompt"
                            className="form-control"
                            value={formData.prompt_id || ''}
                            onChange={(e) => handleInputChange('prompt_id', e.target.value)}
                            disabled={isSubmitting}
                          >
                            <option value="">Select a prompt…</option>
                            {formData.prompt_id
                              && !schedulerPrompts.some((p) => String(p.id) === String(formData.prompt_id)) ? (
                              <option value={formData.prompt_id}>{formData.prompt_id} (saved)</option>
                            ) : null}
                            {schedulerPrompts.map((p) => (
                              <option key={p.id} value={p.id}>
                                {p.name || p.id}
                              </option>
                            ))}
                          </select>
                          {schedulerPromptsError && (
                            <small className="form-error">{schedulerPromptsError}</small>
                          )}
                          <small className="form-help">
                            Uses a project/generic prompt (not Horizon ingest/review/management).
                            Attach tools on that prompt. The scheduler owner’s API keys are used.
                          </small>
                          {errors.prompt_id && <div className="field-error">{errors.prompt_id}</div>}
                        </div>
                        <div className="form-group">
                          <label htmlFor="promptInstructions">Extra instructions (optional)</label>
                          <textarea
                            id="promptInstructions"
                            className="form-control"
                            rows={3}
                            value={formData.prompt_instructions || ''}
                            onChange={(e) => handleInputChange('prompt_instructions', e.target.value)}
                            disabled={isSubmitting}
                            placeholder="Optional extra user message prepended each run"
                          />
                        </div>
                      </>
                    ) : (
                      <ToolChainStepsEditor
                        steps={Array.isArray(formData.chain_steps) ? formData.chain_steps : []}
                        failurePolicy={formData.failure_policy || 'stop'}
                        onChangeSteps={(chain_steps) => handleInputChange('chain_steps', chain_steps)}
                        onChangeFailurePolicy={(failure_policy) => handleInputChange('failure_policy', failure_policy)}
                        disabled={isSubmitting}
                        excludeToolId={tool?.id || null}
                        excludeToolSlug={formData.slug || null}
                        error={errors.chain_steps || null}
                      />
                    )}
                  </>
                )}

                {isWebpageScrape && (
                  <>
                    <div className="form-group">
                      <p className="form-help">
                        Fetches a public URL with browser-like headers and returns extracted page text.
                        If the site blocks server scrapes (403, captcha, bot wall, empty shell), the tool
                        returns <code>denied=true</code> with a stable <code>denial_code</code> so you can
                        monitor and later fall back to archive or device scrape.
                      </p>
                    </div>
                    <div className="form-group">
                      <label htmlFor="scrapeTimeoutMs">Timeout (ms)</label>
                      <input
                        id="scrapeTimeoutMs"
                        type="number"
                        className="form-control"
                        min={1000}
                        max={60000}
                        value={formData.timeout_ms ?? 15000}
                        onChange={(e) => handleInputChange('timeout_ms', e.target.value)}
                        disabled={isSubmitting}
                      />
                    </div>
                    <div className="form-group">
                      <label htmlFor="scrapeMaxChars">Max extracted chars</label>
                      <input
                        id="scrapeMaxChars"
                        type="number"
                        className="form-control"
                        min={1000}
                        max={200000}
                        value={formData.max_chars ?? 50000}
                        onChange={(e) => handleInputChange('max_chars', e.target.value)}
                        disabled={isSubmitting}
                      />
                    </div>
                    <div className="form-group">
                      <label htmlFor="scrapeUserAgent">User-Agent (optional override)</label>
                      <input
                        id="scrapeUserAgent"
                        type="text"
                        className="form-control"
                        value={formData.user_agent || ''}
                        onChange={(e) => handleInputChange('user_agent', e.target.value)}
                        disabled={isSubmitting}
                        placeholder="Leave blank for Chrome desktop default"
                        spellCheck={false}
                      />
                    </div>
                  </>
                )}

                {isHttpRequest && (
                  <>
                    <div className="form-group">
                      <label htmlFor="httpMethod">Method</label>
                      <select
                        id="httpMethod"
                        className="form-control"
                        value={formData.http_method || 'GET'}
                        onChange={(e) => handleInputChange('http_method', e.target.value)}
                        disabled={isSubmitting}
                      >
                        {HTTP_METHODS.map((m) => (
                          <option key={m} value={m}>{m}</option>
                        ))}
                      </select>
                      {errors.http_method && <div className="field-error">{errors.http_method}</div>}
                    </div>
                    <div className="form-group">
                      <label htmlFor="requestUrl">
                        Request URL <span className="required">*</span>
                      </label>
                      <input
                        id="requestUrl"
                        type="text"
                        className="form-control"
                        value={formData.request_url || ''}
                        onChange={(e) => handleInputChange('request_url', e.target.value)}
                        disabled={isSubmitting}
                        placeholder="https://r.jina.ai/ or https://r.jina.ai/{{url}}"
                        spellCheck={false}
                      />
                      <small className="form-help">
                        Outbound host is locked to this URL. Use <code>{'{{url}}'}</code> in the path
                        for GET-style APIs (e.g. Jina <code>https://r.jina.ai/{'{{url}}'}</code>).
                      </small>
                      {errors.request_url && <div className="field-error">{errors.request_url}</div>}
                    </div>
                    <div className="form-group">
                      <label htmlFor="httpHeadersJson">Headers (JSON object)</label>
                      <textarea
                        id="httpHeadersJson"
                        className="form-control"
                        rows={6}
                        value={formData.http_headers_json || '{}'}
                        onChange={(e) => handleInputChange('http_headers_json', e.target.value)}
                        disabled={isSubmitting}
                        placeholder={'{\n  "Content-Type": "application/json"\n}'}
                        spellCheck={false}
                      />
                      <small className="form-help">
                        Header values may include <code>{'{{url}}'}</code>, <code>{'{{search}}'}</code>,
                        and <code>{'{{auth_token}}'}</code>. Prefer the Auth section for credentials
                        instead of putting secrets in headers.
                      </small>
                      {errors.http_headers_json && (
                        <div className="field-error">{errors.http_headers_json}</div>
                      )}
                    </div>
                    <div className="form-group">
                      <label htmlFor="authType">Auth</label>
                      <select
                        id="authType"
                        className="form-control"
                        value={formData.auth_type || 'none'}
                        onChange={(e) => handleInputChange('auth_type', e.target.value)}
                        disabled={isSubmitting}
                      >
                        <option value="none">None</option>
                        <option value="bearer">Bearer token</option>
                        <option value="api_key">API key header</option>
                      </select>
                    </div>
                    {(formData.auth_type === 'bearer' || formData.auth_type === 'api_key') && (
                      <div className="form-group">
                        <label htmlFor="authToken">
                          {formData.auth_type === 'bearer' ? 'Bearer token' : 'API key'}
                        </label>
                        <input
                          id="authToken"
                          type={/\{\{/.test(String(formData.auth_token || '')) ? 'text' : 'password'}
                          className="form-control"
                          value={formData.auth_token || ''}
                          onChange={(e) => handleInputChange('auth_token', e.target.value)}
                          disabled={isSubmitting}
                          placeholder={
                            isEditMode
                              ? 'Leave masked to keep secret, or {{auth_token}} for runtime'
                              : 'Static secret, or {{auth_token}} / {{token}} / {{api_key}}'
                          }
                          autoComplete="new-password"
                          spellCheck={false}
                        />
                        <small className="form-help">
                          Lock in a secret on the tool (write-only), or use{' '}
                          <code>{'{{auth_token}}'}</code> (aliases <code>{'{{token}}'}</code>,{' '}
                          <code>{'{{api_key}}'}</code>) and pass it at chat/runtime. Leave blank to
                          accept a runtime credential only. Leave the masked value unchanged to keep
                          an existing stored secret.
                        </small>
                      </div>
                    )}
                    {formData.auth_type === 'api_key' && (
                      <div className="form-group">
                        <label htmlFor="authHeaderName">API key header name</label>
                        <input
                          id="authHeaderName"
                          type="text"
                          className="form-control"
                          value={formData.auth_header_name || 'X-Api-Key'}
                          onChange={(e) => handleInputChange('auth_header_name', e.target.value)}
                          disabled={isSubmitting}
                          placeholder="X-Api-Key"
                        />
                      </div>
                    )}
                    <div className="form-group">
                      <label htmlFor="bodyTemplate">Body template</label>
                      <textarea
                        id="bodyTemplate"
                        className="form-control"
                        rows={5}
                        value={formData.body_template || ''}
                        onChange={(e) => handleInputChange('body_template', e.target.value)}
                        disabled={isSubmitting}
                        placeholder={'{\n  "url": "{{url}}"\n}'}
                        spellCheck={false}
                      />
                      <small className="form-help">
                        Substitutes <code>{'{{url}}'}</code> (alias <code>{'{{search}}'}</code>) from
                        the chat/runtime argument. Used for POST/PUT/PATCH/DELETE.
                      </small>
                    </div>
                    <div className="form-group">
                      <label htmlFor="timeoutMs">Timeout (ms)</label>
                      <input
                        id="timeoutMs"
                        type="number"
                        min={1000}
                        max={60000}
                        className="form-control"
                        value={formData.timeout_ms ?? 20000}
                        onChange={(e) => handleInputChange('timeout_ms', Number(e.target.value) || 20000)}
                        disabled={isSubmitting}
                      />
                      <small className="form-help">1s–60s (default 20000).</small>
                    </div>
                    <div className="form-group">
                      <label htmlFor="maxBodyChars">Max response body (chars)</label>
                      <input
                        id="maxBodyChars"
                        type="number"
                        min={0}
                        max={1000000}
                        className="form-control"
                        value={formData.max_body_chars ?? 200000}
                        onChange={(e) => {
                          const raw = e.target.value;
                          if (raw === '' || raw == null) {
                            handleInputChange('max_body_chars', 200000);
                            return;
                          }
                          const n = Number(raw);
                          handleInputChange('max_body_chars', Number.isFinite(n) ? n : 200000);
                        }}
                        disabled={isSubmitting}
                      />
                      <small className="form-help">
                        Use <strong>0</strong> for no limit. Otherwise 1000–1,000,000 (default 200000).
                      </small>
                    </div>
                    <div className="form-group">
                      <label htmlFor="httpBatchSize">Batch size (concurrent calls)</label>
                      <input
                        id="httpBatchSize"
                        type="number"
                        min={1}
                        max={100}
                        className="form-control"
                        value={formData.batch_size ?? 1}
                        onChange={(e) => {
                          const n = Number(e.target.value);
                          handleInputChange('batch_size', Number.isFinite(n) && n > 0 ? n : 1);
                        }}
                        disabled={isSubmitting}
                      />
                      <small className="form-help">
                        How many outbound API calls (and ingestion prompt entries) may run at once.
                        Default 1 spaces calls one-after-another. Max 100.
                      </small>
                    </div>
                    <div className="form-group">
                      <label htmlFor="responseAs">Parse response as</label>
                      <select
                        id="responseAs"
                        className="form-control"
                        value={formData.response_as || 'auto'}
                        onChange={(e) => handleInputChange('response_as', e.target.value)}
                        disabled={isSubmitting}
                      >
                        <option value="auto">Auto</option>
                        <option value="text">Text</option>
                        <option value="json">JSON</option>
                      </select>
                    </div>
                  </>
                )}

                {isMcpCall && (
                  <>
                    <div className="form-group">
                      <label htmlFor="mcpServerId">
                        MCP server <span className="required">*</span>
                      </label>
                      <select
                        id="mcpServerId"
                        className="form-control"
                        value={formData.mcp_server_id || ''}
                        onChange={(e) => {
                          handleInputChange('mcp_server_id', e.target.value);
                          handleInputChange('mcp_tool_name', '');
                        }}
                        disabled={isSubmitting}
                      >
                        <option value="">Select an MCP server…</option>
                        {mcpServers.map((s) => (
                          <option key={s.id} value={s.id}>
                            {s.name}
                            {!s.isActive ? ' (inactive)' : ''}
                            {s.isPublic ? ' (public)' : ''}
                          </option>
                        ))}
                      </select>
                      {errors.mcp_server_id && (
                        <small className="form-error">{errors.mcp_server_id}</small>
                      )}
                      <small className="form-help">
                        Configure servers in Settings → User → MCP Servers.
                      </small>
                    </div>

                    <div className="form-group">
                      <label htmlFor="mcpToolName">MCP tool name (optional)</label>
                      <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                        <select
                          id="mcpToolName"
                          className="form-control"
                          value={formData.mcp_tool_name || ''}
                          onChange={(e) => handleInputChange('mcp_tool_name', e.target.value)}
                          disabled={isSubmitting || !formData.mcp_server_id || mcpToolsLoading}
                        >
                          <option value="">Any / choose at runtime</option>
                          {formData.mcp_tool_name
                            && !mcpTools.some((t) => t.name === formData.mcp_tool_name) ? (
                            <option value={formData.mcp_tool_name}>
                              {formData.mcp_tool_name} (saved)
                            </option>
                          ) : null}
                          {mcpTools.map((t) => (
                            <option key={t.name} value={t.name}>
                              {t.name}
                            </option>
                          ))}
                        </select>
                        {Boolean(formData.mcp_tool_name) && (
                          <button
                            type="button"
                            className="btn btn-secondary btn-sm"
                            title="Clear pinned MCP tool"
                            onClick={() => handleInputChange('mcp_tool_name', '')}
                            disabled={isSubmitting}
                          >
                            ×
                          </button>
                        )}
                      </div>
                      {mcpToolsLoading && (
                        <small className="form-help">
                          <i className="fas fa-spinner fa-spin" /> Loading tools from MCP…
                        </small>
                      )}
                      {mcpToolsError && (
                        <small className="form-error">{mcpToolsError}</small>
                      )}
                    </div>

                    <div className="form-group">
                      <label htmlFor="mcpDefaultArgs">Default arguments JSON (optional)</label>
                      <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
                        <textarea
                          id="mcpDefaultArgs"
                          className="form-control"
                          rows={3}
                          value={formData.default_arguments || ''}
                          onChange={(e) => handleInputChange('default_arguments', e.target.value)}
                          disabled={isSubmitting}
                          placeholder='{"query":"..."}'
                        />
                        {Boolean(formData.default_arguments) && (
                          <button
                            type="button"
                            className="btn btn-secondary btn-sm"
                            title="Clear default arguments"
                            onClick={() => handleInputChange('default_arguments', '')}
                            disabled={isSubmitting}
                          >
                            ×
                          </button>
                        )}
                      </div>
                      <small className="form-help">
                        Leave empty to clear. Chat may override with runtime arguments.
                      </small>
                    </div>

                    {mcpDebug && (
                      <div className="form-group">
                        <label>MCP connection debug</label>
                        <pre
                          className="form-control"
                          style={{
                            maxHeight: 220,
                            overflow: 'auto',
                            fontSize: 11,
                            whiteSpace: 'pre-wrap',
                            background: '#0f172a',
                            color: '#e2e8f0',
                          }}
                        >
{JSON.stringify(
  {
    serverName: mcpDebug.serverName,
    transport: mcpDebug.transport,
    toolCount: mcpDebug.toolCount,
    durationMs: mcpDebug.durationMs,
    tools: (mcpDebug.tools || []).map((t) => ({
      name: t.name,
      description: t.description || '',
    })),
  },
  null,
  2
)}
                        </pre>
                      </div>
                    )}
                  </>
                )}

                {isEmailDraft && (
                  <>
                    <div className="form-group">
                      <label htmlFor="emailAction">Email action</label>
                      <select
                        id="emailAction"
                        className="form-control"
                        value={formData.email_action || 'save'}
                        onChange={(e) => handleInputChange('email_action', e.target.value)}
                        disabled={isSubmitting}
                      >
                        {EMAIL_DRAFT_ACTIONS.map((a) => (
                          <option key={a.id} value={a.id}>{a.label}</option>
                        ))}
                      </select>
                      <small className="form-help">
                        Projects also include account email draft tools automatically in chat.
                        Gmail requires Connect Google with gmail.compose.
                      </small>
                    </div>
                    <div className="form-group">
                      <label htmlFor="emailDefaultTone">Default tone (optional)</label>
                      <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                        <input
                          id="emailDefaultTone"
                          className="form-control"
                          value={formData.default_tone || ''}
                          onChange={(e) => handleInputChange('default_tone', e.target.value)}
                          disabled={isSubmitting}
                          placeholder="polite, firm, final_notice…"
                        />
                        {Boolean(formData.default_tone) && (
                          <button
                            type="button"
                            className="btn btn-secondary btn-sm"
                            title="Clear default tone"
                            onClick={() => handleInputChange('default_tone', '')}
                            disabled={isSubmitting}
                          >
                            ×
                          </button>
                        )}
                      </div>
                    </div>
                  </>
                )}

                {isDataBackedTool && selectedGroupSlugs.length > 0 && (
                  <>
                    {keysLoading && (
                      <small className="form-help">Loading shared field keys for selected groups…</small>
                    )}
                    {!isEntryWriteTool && (
                    <div className="form-group">
                      <label htmlFor="searchField">
                        Search field {!isExactKeyTool && <span className="required">*</span>}
                      </label>
                      <FieldSelectModule
                        value={formData.search_field}
                        onChange={(value) => handleInputChange('search_field', value)}
                        mode="single"
                        fieldType="search"
                        allowClear={isExactKeyTool}
                        placeholder={
                          isExactKeyTool
                            ? 'Optional — leave empty if unused'
                            : 'Select a field shared by all groups…'
                        }
                        disabled={isSubmitting || keysLoading}
                        error={fieldErrors.search_field}
                        onError={(error) => handleFieldError('search_field', error)}
                        onClearError={() => clearFieldError('search_field')}
                        fieldsOverride={groupRawFields}
                        fileKeysConvertToText={fileKeysConvertToText}
                      />
                      {isExactKeyTool && (
                        <small className="form-help">
                          Optional for filter/analytics. Unused by filter predicates — leave it empty.
                          Filter JSON keys do not have to appear here.
                        </small>
                      )}
                      {selectedGroupSlugs.length > 1 && (
                        <small className="form-help">
                          Only fields present in all {selectedGroupSlugs.length} groups are listed.
                        </small>
                      )}
                      {groupMetaFromKeys && !groupMetaFromKeys.multi && (
                        <small className="form-help" style={{ display: 'block', marginTop: '6px' }}>
                          Group{groupMetaFromKeys.name ? ` “${groupMetaFromKeys.name}”` : ''} is{' '}
                          {groupMetaFromKeys.is_private ? 'private.' : 'public.'}
                        </small>
                      )}
                    </div>
                    )}

                    <div className="form-group">
                      <label htmlFor="returnFields">
                        {isEntryWriteTool ? 'Writable fields' : 'Return fields'}{' '}
                        {needsWritableAllowlist && <span className="required">*</span>}
                      </label>
                      <FieldSelectModule
                        value={formData.return_fields}
                        onChange={(value) => handleInputChange('return_fields', value)}
                        mode="multiple"
                        fieldType="return"
                        placeholder="Select fields shared by all groups…"
                        disabled={isSubmitting || keysLoading}
                        error={fieldErrors.return_fields}
                        onError={(error) => handleFieldError('return_fields', error)}
                        onClearError={() => clearFieldError('return_fields')}
                        fieldsOverride={groupReturnFields}
                      />
                      {isFilterTool && (
                        <small className="form-help">
                          Columns included on each result row. Predicates in Default filter arguments
                          (and chat/scheduler JSON) may use <code>raw.*</code> and <code>intrinsic.*</code>
                          even when those keys are not listed here. Unprefixed names that are not return
                          fields mean <code>raw.&lt;name&gt;</code> (so <code>status</code> is{' '}
                          <code>raw.status</code>). Search field is unused for filtering.
                        </small>
                      )}
                      {isFilterTool && (
                        <div className="form-group" style={{ marginTop: 12 }}>
                          <label htmlFor="filterDefaultArgs">Default filter arguments JSON (optional)</label>
                          <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
                            <textarea
                              id="filterDefaultArgs"
                              className="form-control"
                              rows={5}
                              value={formData.default_arguments || ''}
                              onChange={(e) => handleInputChange('default_arguments', e.target.value)}
                              disabled={isSubmitting}
                              placeholder={'{\n  "filters": [{ "key": "raw.status", "op": "eq", "value": "New" }],\n  "order_by": [{ "key": "raw.created_at", "dir": "asc" }],\n  "limit": 1\n}'}
                            />
                            {Boolean(formData.default_arguments) && (
                              <button
                                type="button"
                                className="btn btn-secondary btn-sm"
                                title="Clear default arguments"
                                onClick={() => handleInputChange('default_arguments', '')}
                                disabled={isSubmitting}
                              >
                                ×
                              </button>
                            )}
                          </div>
                          <small className="form-help">
                            Used when this tool runs without a live <code>filters</code> argument
                            (scheduler and tool-chain steps). Chat can still override. Human-language
                            descriptions are not executed as filters.
                          </small>
                        </div>
                      )}
                      {isEntryUpdate && (
                        <small className="form-help">
                          Writable keys for this tool. Prefer <code>raw.*</code> for ingested columns.
                          Include lookup keys (for example <code>article_id</code>) even if you only
                          write other fields. With an <code>entry_id</code> (or unique lock / previous
                          step / filter match) the tool updates that row. Without an{' '}
                          <code>entry_id</code> it creates a new row when “Allow create” is on.
                          In chat the user confirms; on a scheduler confirmation is skipped.
                        </small>
                      )}
                      {isEntryCreate && (
                        <small className="form-help">
                          Writable keys for the new row. Prefer <code>raw.*</code> for ingested columns.
                          This tool always creates a new entry with a generated <code>entry_id</code>.
                          In chat the user confirms; on a scheduler confirmation is skipped.
                        </small>
                      )}
                    </div>
                  </>
                )}

                {isEntryUpdate && (
                  <div className="form-group entry-update-allow-create">
                    <label className="checkbox-row" style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer' }}>
                      <input
                        type="checkbox"
                        checked={formData.allow_create !== false}
                        onChange={(e) => handleInputChange('allow_create', e.target.checked)}
                        disabled={isSubmitting}
                        style={{ width: '18px', height: '18px' }}
                      />
                      <span>Allow create when entry_id is omitted</span>
                    </label>
                    <small className="form-help">
                      When checked, missing <code>entry_id</code> (and zero filter matches) creates a
                      new row with a generated id. When unchecked, an existing target is required.
                    </small>
                  </div>
                )}

                {isEntryCreate && (
                  <div className="form-group entry-create-hint">
                    <small className="form-help">
                      Entry create never updates existing rows. Use <strong>Entry update</strong> when you
                      need upsert / lookup behavior.
                    </small>
                  </div>
                )}

                <YumlConfigFields
                  suffix={toolSuffix}
                  value={formData.yuml_config}
                  onChange={(yuml_config) => setFormData((prev) => ({ ...prev, yuml_config }))}
                  summaryKeyOptions={Array.isArray(formData.return_fields) ? formData.return_fields : []}
                  emitOn="after_output"
                />

                <div className="form-group">
                  <label style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      checked={formData.is_private}
                      onChange={(e) => handleInputChange('is_private', e.target.checked)}
                      disabled={isSubmitting}
                      style={{ width: '18px', height: '18px' }}
                    />
                    <span>
                      <i className="fas fa-lock" /> Private (only visible to admins and owner)
                    </span>
                  </label>
                </div>
              </div>
            </div>

            <div className="modal-footer">
              <button type="button" className="btn btn-secondary" onClick={handleClose} disabled={isSubmitting}>
                Cancel
              </button>
              <button
                type="submit"
                className="btn btn-primary"
                disabled={isSubmitting || Object.keys(errors).length > 0 || !!slugConflict}
              >
                {isSubmitting && <i className="fas fa-spinner fa-spin" />}
                {isEditMode ? 'Update' : 'Create'} Tool
              </button>
            </div>
          </form>
        </div>
      </div>

      <ToolCollisionConfirmModal
        isOpen={collisionModalOpen}
        collisions={pendingCollisions.collisions}
        pairs={pendingCollisions.pairs}
        onSave={handleCollisionSave}
        onFix={handleCollisionFix}
        saving={isSubmitting}
      />
    </>
  );
}

export default ToolEditor;
